import { type PortableTextBlock, toPlainText } from 'next-sanity'

import { TocLink } from '@/app/components/TocLink'
import { ARTICLE_BLOCK_SPACING } from '@/app/utils/articleBlock'
import { cn } from '@/app/utils/style'
import { generateElementId } from '@/app/utils/text'

/**
 * `numbered` (site pages): lists headings of any level that start with "N. ", e.g. the privacy policy.
 * `chapters` (blog articles): h2 headings are the chapters, h3 headings nest under the h2 above them.
 * Both read the same block data, so the page decides which one applies.
 */
export type TableOfContentsVariant = 'numbered' | 'chapters'

type HeadingItem = {
  text: string
  id: string
  children: HeadingItem[]
}

type TableOfContentsProps = {
  allBlocks: PortableTextBlock[]
  variant?: TableOfContentsVariant
}

const HEADING_STYLES = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6']
const NUMBERED_HEADING = /^\d+\.\s+(.+)$/

const CHAPTER_STYLE = 'h2'
const SUBCHAPTER_STYLE = 'h3'
// Very short articles get no table of contents
const MIN_CHAPTER_ENTRIES = 5

const headingText = (block: PortableTextBlock): string | null =>
  block._type === 'block' && block.children ? toPlainText(block).trim() || null : null

function extractNumberedHeadings(blocks: PortableTextBlock[]): HeadingItem[] {
  return blocks.flatMap((block) => {
    const text = headingText(block)
    const match =
      text && block.style && HEADING_STYLES.includes(block.style) && NUMBERED_HEADING.exec(text)
    // The id keeps the number so it matches the id the heading renders with
    return match ? [{ text: match[1], id: generateElementId(text), children: [] }] : []
  })
}

function extractChapters(blocks: PortableTextBlock[]): HeadingItem[] {
  const chapters: HeadingItem[] = []

  blocks.forEach((block) => {
    if (block.style !== CHAPTER_STYLE && block.style !== SUBCHAPTER_STYLE) return
    const text = headingText(block)
    if (!text) return

    const item: HeadingItem = { text, id: generateElementId(text), children: [] }
    const parent = chapters[chapters.length - 1]
    if (block.style === SUBCHAPTER_STYLE && parent) parent.children.push(item)
    else chapters.push(item)
  })

  return chapters
}

const countEntries = (items: HeadingItem[]): number =>
  items.reduce((total, item) => total + 1 + item.children.length, 0)

function NumberedTableOfContents({ headings }: { headings: HeadingItem[] }) {
  return (
    <>
      <p className="size-body-xl text-white-800 mt-5xl mb-0">Table of contents</p>
      <nav className="my-3xl lg:my-5xl" aria-label="Table of contents">
        <ol className="flex flex-col gap-md [counter-reset:item] [&_ol]:mt-md [&_ol]:ml-0 md:[&_ol]:ml-md [&_ol]:my-0 [&_ol]:[counter-reset:item]">
          {headings.map((heading, index) => (
            <li
              key={`${heading.id}-${index}`}
              className="flex gap-sm items-start [counter-increment:item] before:content-[counter(item)'.'] before:text-green-600 before:font-bold before:min-w-[1.5rem] before:leading-[20px] before:pt-[2px] before:items-start [ol_&]:before:content-[counters(item,'.')'.']"
            >
              <p className="size-body-sm lg:size-body-md mb-0 [&_a]:font-medium [&_a]:text-green-600">
                <a href={`#${heading.id}`}>{heading.text}</a>
              </p>
            </li>
          ))}
        </ol>
      </nav>
    </>
  )
}

function ChapterTableOfContents({ chapters }: { chapters: HeadingItem[] }) {
  return (
    <nav
      aria-label="Table of contents"
      className={cn(
        ARTICLE_BLOCK_SPACING,
        'mx-auto w-fit max-w-full rounded-xs border border-neutral-200 bg-neutral-50 py-md pr-2xl pl-md',
      )}
    >
      <p className="size-body-xl mb-0 text-white-950">In this blog:</p>
      <ol className="mt-sm flex flex-col">
        {chapters.map((chapter) => (
          <li key={chapter.id}>
            <TocLink
              id={chapter.id}
              className="toc-link size-body-md block py-xs text-white-900 hover:underline"
            >
              {chapter.text}
            </TocLink>
            {chapter.children.length > 0 && (
              <ol className="ml-4xl flex flex-col">
                {chapter.children.map((child) => (
                  <li key={child.id}>
                    <TocLink
                      id={child.id}
                      className="toc-link size-body-md block py-xs text-white-900 hover:underline"
                    >
                      {child.text}
                    </TocLink>
                  </li>
                ))}
              </ol>
            )}
          </li>
        ))}
      </ol>
    </nav>
  )
}

export function TableOfContents({ allBlocks, variant = 'numbered' }: TableOfContentsProps) {
  if (variant === 'chapters') {
    const chapters = extractChapters(allBlocks)
    return countEntries(chapters) < MIN_CHAPTER_ENTRIES ? null : (
      <ChapterTableOfContents chapters={chapters} />
    )
  }

  const headings = extractNumberedHeadings(allBlocks)
  return headings.length === 0 ? null : <NumberedTableOfContents headings={headings} />
}
